import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { Send, Paperclip, X, Loader2, Bot } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

import { cn, getProjectDisplayName } from '../lib/utils';
import { BackendAPI } from '../lib/api';
import useProjectStore from '../stores/projectStore';
import { ChatMessage, Project } from '../types';

import { Button } from '../components/ui/Button';
import { Textarea } from '../components/ui/Textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/Card';

// ================================================================= //
//          AI Chat Page Component                                   //
// ================================================================= //
// A comprehensive chat interface for interacting with the AI        //
// Estimating Assistant. It supports text, images, and automatically //
// includes project context for location-aware responses.            //
// ================================================================= //

export default function ChatPage() {
  const { projectId } = useParams<{ projectId?: string }>();
  const { getProjectById } = useProjectStore();

  const [project, setProject] = useState<Project | null>(null);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState('');
  const [image, setImage] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const chatEndRef = useRef<HTMLDivElement>(null);

  // --- Effects ---

  // Fetch project details when projectId changes
  useEffect(() => {
    const fetchProjectData = async () => {
      if (projectId) {
        const fetchedProject = await getProjectById(projectId);
        if (fetchedProject) {
          setProject(fetchedProject);
        }
      } else {
        setProject(null);
      }
    };
    fetchProjectData();
  }, [projectId, getProjectById]);

  // Scroll to the bottom of the chat on new messages
  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // --- Event Handlers ---

  const handleSendMessage = async () => {
    if ((!input.trim() && !image) || isLoading) return;

    const userMessage: ChatMessage = {
      id: Date.now().toString(),
      sender: 'user',
      text: input,
      image_url: imagePreview || undefined,
      timestamp: Date.now(),
    };

    setMessages((prev) => [...prev, userMessage]);
    setInput('');
    setImage(null);
    setImagePreview(null);
    setIsLoading(true);
    setError(null);

    try {
      const formData = new FormData();
      formData.append('question', input);
      if (image) {
        formData.append('image', image);
      }
      if (projectId) {
        formData.append('project_id', projectId);
      }
      // Send the address for location context
      if (project?.address) {
        formData.append('project_address', project.address);
      }

      const response = await BackendAPI.sendMessageToChatbot(formData);

      const aiMessage: ChatMessage = {
        id: (Date.now() + 1).toString(),
        sender: 'ai',
        text: response.answer,
        scope_data: response.scope_data,
        timestamp: Date.now(),
      };
      setMessages((prev) => [...prev, aiMessage]);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'An unknown error occurred.';
      setError(errorMessage);
      const errorMsg: ChatMessage = {
        id: (Date.now() + 1).toString(),
        sender: 'ai',
        text: `Sorry, I encountered an error: ${errorMessage}`,
        timestamp: Date.now(),
      };
      setMessages((prev) => [...prev, errorMsg]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleImageSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      setImage(file);
      setImagePreview(URL.createObjectURL(file));
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  // --- Render Logic ---

  return (
    <div className="flex h-full flex-col bg-white">
      {/* Chat Header */}
      <div className="border-b p-4">
        <h2 className="text-lg font-semibold text-primary">AI Assistant Chat</h2>
        {project && (
          <p className="text-sm text-muted-foreground">
            Context: <span className="font-medium text-foreground">{getProjectDisplayName(project)}</span>
          </p>
        )}
      </div>

      {/* Chat Messages Area */}
      <div className="flex-1 overflow-y-auto p-4">
        <div className="mx-auto max-w-4xl space-y-6">
          {messages.length === 0 && (
            <div className="text-center text-muted-foreground">
              <Bot className="mx-auto h-12 w-12" />
              <p className="mt-2">Ask me anything about this project.</p>
            </div>
          )}
          {messages.map((msg) => (
            <div
              key={msg.id}
              className={cn(
                'flex items-start gap-4',
                msg.sender === 'user' ? 'justify-end' : 'justify-start'
              )}
            >
              {msg.sender === 'ai' && (
                <div className="flex h-8 w-8 items-center justify-center rounded-full bg-secondary text-secondary-foreground">
                  <Bot size={20} />
                </div>
              )}
              <div
                className={cn(
                  'max-w-xl rounded-lg p-3',
                  msg.sender === 'user'
                    ? 'rounded-br-none bg-primary text-primary-foreground'
                    : 'rounded-bl-none bg-slate-100 text-foreground'
                )}
              >
                <ReactMarkdown
                  remarkPlugins={[remarkGfm]}
                  components={{
                    p: ({node, ...props}) => <p className="mb-2 last:mb-0" {...props} />,
                    h3: ({node, ...props}) => <h3 className="text-md font-semibold mt-4 mb-2" {...props} />,
                    ul: ({node, ...props}) => <ul className="list-disc pl-5 mb-2" {...props} />,
                    ol: ({node, ...props}) => <ol className="list-decimal pl-5 mb-2" {...props} />,
                    code: ({node, ...props}) => <code className="bg-slate-200 rounded px-1 text-sm font-mono" {...props} />,
                  }}
                >
                  {msg.text}
                </ReactMarkdown>
                {msg.image_url && (
                  <img src={msg.image_url} alt="User upload" className="mt-2 max-w-xs rounded-lg" />
                )}
              </div>
            </div>
          ))}
          {isLoading && (
            <div className="flex items-start gap-4 justify-start">
              <div className="flex h-8 w-8 items-center justify-center rounded-full bg-secondary text-secondary-foreground">
                <Bot size={20} />
              </div>
              <div className="flex items-center space-x-1 rounded-lg bg-slate-100 p-3">
                <span className="h-2 w-2 animate-bounce rounded-full bg-slate-400 [animation-delay:-0.3s]" />
                <span className="h-2 w-2 animate-bounce rounded-full bg-slate-400 [animation-delay:-0.15s]" />
                <span className="h-2 w-2 animate-bounce rounded-full bg-slate-400" />
              </div>
            </div>
          )}
          <div ref={chatEndRef} />
        </div>
      </div>

      {/* Chat Input Area */}
      <div className="border-t bg-slate-50 p-4">
        <div className="mx-auto max-w-4xl">
          {imagePreview && (
            <div className="relative mb-2 inline-block">
              <img src={imagePreview} alt="Preview" className="h-20 w-20 rounded-lg object-cover" />
              <button
                onClick={() => {
                  setImage(null);
                  setImagePreview(null);
                }}
                className="absolute -top-2 -right-2 rounded-full bg-slate-600 p-1 text-white"
              >
                <X size={14} />
              </button>
            </div>
          )}
          <div className="relative flex items-end">
            <Textarea
              ref={fileInputRef as any}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="Ask about building codes, design wind speeds, or generate a scope..."
              className="resize-none pr-24"
              rows={1}
              disabled={isLoading}
            />
            <div className="absolute bottom-2 right-2 flex items-center gap-2">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => fileInputRef.current?.click()}
                disabled={isLoading}
                aria-label="Attach image"
              >
                <Paperclip className="h-5 w-5 text-muted-foreground" />
              </Button>
              <Button
                onClick={handleSendMessage}
                disabled={(!input.trim() && !image) || isLoading}
                isLoading={isLoading}
              >
                <Send className="h-5 w-5" />
              </Button>
            </div>
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleImageSelect}
              className="hidden"
              accept="image/*"
            />
          </div>
        </div>
      </div>
    </div>
  );
}
